/**
 * Author: Codex CLI 助手
 * Generation: Global loading overlay (server+client progress merge)
 * Version: v3.2.169
 * Modified: 2025-10-25
 */
import { useEffect, useRef, useState } from 'react';
import progressAggregator from '../services/progress/ProgressAggregator';
import webSocketService from '../services/websocket/WebSocketService';

// Global overlay that listens to GAME_LOADING_* + SYSTEM_STATE_SYNC and
// BattleScene asset readiness to provide a single, smooth loading bar.
export default function GlobalLoadingOverlay() {
  const [visible, setVisible] = useState(false);
  const [percentage, setPercentage] = useState(0);
  const [message, setMessage] = useState('Preparing to load...');
  const [snapshotReceived, setSnapshotReceived] = useState(false);
  const [assetsReady, setAssetsReady] = useState(false);
  const targetRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    let mounted = true;
    let closing = false;

    const maybeClose = () => {
      if (!mounted || closing) return;
      if (snapshotReceived && assetsReady) {
        closing = true;
        targetRef.current = 100;
        setPercentage(100);
        setMessage('Starting game...');
        setTimeout(() => { if (mounted) setVisible(false); }, 300);
        if (timerRef.current != null) { clearInterval(timerRef.current); timerRef.current = null; }
      }
    };

    // 订阅聚合进度（客户端资源 + 服务器阶段）
    const unsubscribe = progressAggregator.subscribe((combined, detail) => {
      if (!mounted) return;
      targetRef.current = combined;
      if (timerRef.current == null) {
        timerRef.current = window.setInterval(() => {
          setPercentage((prev) => {
            const hardTarget = targetRef.current;
            const softTarget = Math.min(95, Math.max(hardTarget, prev + 0.5));
            if (softTarget <= prev) return prev;
            const step = Math.max(0.5, (softTarget - prev) * 0.15);
            return Math.min(prev + step, softTarget);
          });
        }, 80) as unknown as number;
      }
      if (detail?.client >= 100) setAssetsReady(true);
      maybeClose();
    });

    // WebSocket 事件绑定（一次性，正确清理）
    const onStarted = (data: any) => {
      if (!mounted) return;
      try { progressAggregator.reset(); } catch {}
      setVisible(true);
      setPercentage(0);
      setMessage('Preparing to load...');
      targetRef.current = 0;
      if (timerRef.current != null) { clearInterval(timerRef.current); timerRef.current = null; }
    };
    const onProgress = (data: any) => {
      if (!mounted) return;
      const p = Math.floor(data?.percentage ?? 0);
      try { progressAggregator.setServerProgress(p); } catch {}
      setMessage(data?.message || 'Loading...');
    };
    const onCompleted = (_: any) => {
      targetRef.current = Math.max(targetRef.current, 95);
      setMessage('Finalizing...');
    };
    const onSnapshot = (_snapshot: any) => {
      setSnapshotReceived(true);
      maybeClose();
    };
    const onMapGenerated = (_payload: any) => {};

    webSocketService.on('GAME_LOADING_STARTED', onStarted);
    webSocketService.on('GAME_LOADING_PROGRESS', onProgress);
    webSocketService.on('GAME_LOADING_COMPLETED', onCompleted);
    webSocketService.on('SYSTEM_STATE_SYNC', onSnapshot);
    webSocketService.on('MAP_GENERATED', onMapGenerated);

    const onMapReady = () => { setAssetsReady(true); maybeClose(); };
    window.addEventListener('ABR_MAP_READY', onMapReady);

    return () => {
      mounted = false;
      try {
        webSocketService.off('GAME_LOADING_STARTED', onStarted);
        webSocketService.off('GAME_LOADING_PROGRESS', onProgress);
        webSocketService.off('GAME_LOADING_COMPLETED', onCompleted);
        webSocketService.off('SYSTEM_STATE_SYNC', onSnapshot);
        webSocketService.off('MAP_GENERATED', onMapGenerated);
      } catch {}
      window.removeEventListener('ABR_MAP_READY', onMapReady);
      unsubscribe?.();
      if (timerRef.current != null) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, []);

  if (!visible) return null;

  // UI mirrors LoadingScreen visuals to keep consistent styling
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div className="hero-content">
        <h1 className="hero-title" style={{ marginBottom: 'var(--space-12)' }}>Agent Battle Royale 3.0</h1>
        <p className="hero-subtitle" style={{ marginBottom: 'var(--space-16)' }}>Loading Game...</p>
        <div style={{ position: 'relative', width: '600px', marginBottom: 'var(--space-8)', marginLeft: 'auto', marginRight: 'auto' }}>
          <div style={{ height: '40px', background: '#222222', border: '2px solid #444444', borderRadius: 'var(--radius-lg)', boxShadow: '0 4px 6px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${percentage}%`, background: percentage >= 80 ? '#4ade80' : (percentage >= 40 ? '#60a5fa' : '#4a9eff'), transition: 'width 0.1s ease-linear' }} />
          </div>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-3xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text-primary)', textShadow: '0 2px 4px rgba(0,0,0,0.8)', pointerEvents: 'none' }}>{Math.round(percentage)}%</div>
        </div>
        <p style={{ fontSize: 'var(--font-lg)', color: 'var(--color-text-secondary)', textAlign: 'center', minHeight: '28px' }}>{message}</p>
      </div>
    </div>
  );
}
